import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const StudentGradesModal = ({
  student,
  gradesData,
  sessionsData,
  closeModal,
  classId,
  theme,
}) => {
  // Access the student's grades from the grades data
  const studentGrades = gradesData?.students?.[student.uid] || {};

  const [expandedSessions, setExpandedSessions] = useState({});
  const [editing, setEditing] = useState(null); // { sessionId, questionNumber }
  const [editedPoint, setEditedPoint] = useState(null);

  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  const handleEditClick = (sessionId, questionNumber, currentPoint) => {
    setEditing({ sessionId, questionNumber });
    setEditedPoint(currentPoint);
  };

  const handleSaveClick = async () => {
    const { sessionId, questionNumber } = editing;
    try {
      await updateDoc(doc(db, 'grades', classId), {
        [`students.${student.uid}.${sessionId}.points.${questionNumber}`]:
          Number(editedPoint),
      });
      // Update local state (in a real app, you might want to refresh data)
      gradesData.students[student.uid][sessionId].points[questionNumber] =
        Number(editedPoint);
      setEditing(null);
      setEditedPoint(null);
    } catch (error) {
      console.error('Error updating grade:', error);
    }
  };

  const handleCancelClick = () => {
    setEditing(null);
    setEditedPoint(null);
  };

  // Calculate total points for this student
  const totalPoints = Object.values(studentGrades).reduce(
    (sum, session) =>
      sum + Object.values(session.points || {}).reduce((s, p) => s + p, 0),
    0
  );

  // Prepare sessions list
  const sessionEntries = sessionsData
    ? Array.isArray(sessionsData)
      ? sessionsData.map((session) => [session.id, session])
      : Object.entries(sessionsData)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    {/* Dark backdrop */}
    <div
      className="fixed inset-0"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={closeModal}
    ></div>
  
    {/* Modal container */}
    <div
      className="relative w-full max-w-3xl mx-auto rounded-lg shadow-lg overflow-hidden"
      style={{
        backgroundColor: 'var(--section-bg)',
        color: 'var(--text-color)',
      }}
    >
      {/* Header */}
      <div
        className="p-4 relative"
        style={{ borderBottom: '1px solid var(--muted-color)' }}
      >
        <h5 className="text-xl font-bold">
          Grades for {student.firstName} {student.lastName} (Total Points: {totalPoints})
        </h5>
        <button
          type="button"
          onClick={closeModal}
          style={{ color: 'var(--muted-color)' }}
          onMouseOver={(e) =>
            (e.currentTarget.style.color = 'var(--nav-active-text)')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.color = 'var(--muted-color)')
          }
          className="absolute top-4 right-4 text-2xl font-bold focus:outline-none"
        >
          &times;
        </button>
      </div>
  
      {/* Body */}
      <div className="p-4 max-h-[80vh] overflow-auto">
        <table className="min-w-full divide-y divide-transparent">
          <thead style={{ backgroundColor: 'var(--card-bg)' }}>
            <tr>
              <th
                className="px-4 py-2 text-left text-sm font-medium"
                style={{ color: 'var(--text-color)' }}
              >
                Session Date
              </th>
              <th
                className="px-4 py-2 text-left text-sm font-medium"
                style={{ color: 'var(--text-color)' }}
              >
                Total Points
              </th>
              <th
                className="px-4 py-2 text-left text-sm font-medium"
                style={{ color: 'var(--text-color)' }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-transparent">
            {sessionEntries.map(([sessionId, session]) => {
              const sessionData = studentGrades[sessionId];
              const sessionDate = session?.start_date_time
                ? new Date(session.start_date_time).toLocaleDateString()
                : sessionId;
              const sessionTotalPoints = sessionData
                ? Object.values(sessionData.points || {}).reduce(
                    (sum, p) => sum + p,
                    0
                  )
                : 'Not attended';
              const isExpanded = expandedSessions[sessionId];
  
              return (
                <React.Fragment key={sessionId}>
                  <tr style={{ backgroundColor: 'var(--gradient-color)' }}>
                    <td className="px-4 py-2" style={{ color: 'var(--text-color)' }}>
                      {sessionDate}
                    </td>
                    <td className="px-4 py-2" style={{ color: 'var(--text-color)' }}>
                      {sessionTotalPoints}
                    </td>
                    <td className="px-4 py-2">
                      {sessionData ? (
                        <button
                          onClick={() => toggleSession(sessionId)}
                          className="px-3 py-1 text-sm rounded border transition-colors"
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: 'var(--primary-color)',
                            color: 'var(--primary-color)',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor =
                              'var(--hover-color)';
                            e.currentTarget.style.color =
                              'var(--nav-active-text)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                            e.currentTarget.style.color =
                              'var(--primary-color)';
                          }}
                        >
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </button>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  </tr>
                  {isExpanded &&
                    sessionData &&
                    Object.entries(sessionData.points || {}).map(
                      ([questionNumber, point]) => (
                        <tr
                          key={`${sessionId}-${questionNumber}`}
                          style={{ backgroundColor: 'var(--alt-bg)' }}
                        >
                          <td
                            className="px-4 py-2 pl-8"
                            style={{ color: 'var(--text-color)' }}
                          >
                            Question {questionNumber}
                          </td>
                          <td
                            className="px-4 py-2"
                            style={{ color: 'var(--text-color)' }}
                          >
                            {editing &&
                            editing.sessionId === sessionId &&
                            editing.questionNumber === questionNumber ? (
                              <input
                                type="number"
                                value={editedPoint}
                                onChange={(e) => setEditedPoint(e.target.value)}
                                className="w-20 px-2 py-1 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                                style={{ borderColor: 'var(--primary-color)' }}
                              />
                            ) : (
                              point
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {editing &&
                            editing.sessionId === sessionId &&
                            editing.questionNumber === questionNumber ? (
                              <div className="flex space-x-2">
                                <button
                                  className="px-3 py-1 text-sm rounded text-white transition-colors"
                                  onClick={handleSaveClick}
                                  onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      'var(--hover-color)')
                                  }
                                  onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      'var(--primary-color)')
                                  }
                                  style={{
                                    backgroundColor: 'var(--primary-color)',
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  className="px-3 py-1 text-sm rounded text-white transition-colors"
                                  onClick={handleCancelClick}
                                  onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      'var(--hover-color)')
                                  }
                                  onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      'var(--primary-color)')
                                  }
                                  style={{
                                    backgroundColor: 'var(--primary-color)',
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                className="px-3 py-1 text-sm rounded text-white transition-colors"
                                onClick={() =>
                                  handleEditClick(
                                    sessionId,
                                    questionNumber,
                                    point
                                  )
                                }
                                onMouseOver={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    'var(--hover-color)')
                                }
                                onMouseOut={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    'var(--primary-color)')
                                }
                                style={{
                                  backgroundColor: 'var(--primary-color)',
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
  
      {/* Footer */}
      <div
        className="p-4 flex justify-end"
        style={{ borderTop: '1px solid var(--muted-color)' }}
      >
        <button
          type="button"
          onClick={closeModal}
          className="px-4 py-2 rounded text-white transition-colors"
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
          }
          style={{ backgroundColor: 'var(--primary-color)' }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
  );  
};

export default StudentGradesModal;
